import { DateTime } from 'luxon';
import {
  CallOutcome,
  ITeamActivityCounts,
  ITeamActivityWeek,
} from 'shared';

import { IDelta, rate } from '../EmailPerformance/stats';

export function activities(c: ITeamActivityCounts) {
  return c.phoneCalls + c.textMessages + c.externalEmails;
}

export function weekActivities(w: ITeamActivityWeek) {
  return w.phoneCalls + w.textMessages + w.externalEmails;
}

export function reachedRate(c: ITeamActivityCounts) {
  return rate(c.callOutcomes[CallOutcome.SPOKE_TO_CUSTOMER] ?? 0, c.phoneCalls);
}

export function keptRate(c: ITeamActivityCounts) {
  return rate(c.promisesKept, c.promisesKept + c.promisesBroken);
}

export function inEscalationRate(c: ITeamActivityCounts) {
  return rate(c.inEscalations, activities(c));
}

/** Rate for the 0-100 scale, "-" when there is nothing to divide by */
export function pct(value: number | null, dp = 0) {
  return value === null ? `-` : `${value.toFixed(dp)}%`;
}

export function formatTalkTime(seconds: number) {
  const totalMinutes = Math.round(seconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Whole-number change between two counts */
export function countDelta(current: number, previous: number): IDelta {
  const diff = current - previous;

  return { label: String(Math.abs(diff)), direction: diff > 0 ? `up` : diff < 0 ? `down` : `flat` };
}

/** "↑ 14.0%". The arrow carries the direction, so the sign on the label is dropped */
export function deltaText(delta: IDelta | null) {
  if (!delta) {
    return ``;
  }

  const arrow = delta.direction === `up` ? `↑` : delta.direction === `down` ? `↓` : `→`;

  return `${arrow} ${delta.label.replace(/^[+−-]/, ``)}`;
}

/** Top of a chart axis: 4 even steps of 1, 2, 5 or 10 times a power of ten that cover max */
export function niceTop(max: number) {
  const raw = Math.max(max, 4) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 5, 10].find(s => s * mag >= raw) ?? 10) * mag;

  return step * 4;
}

export interface ISparkGeometry {
  line: string;
  area: string;
  last: { x: number; y: number };
}

/** Line and area paths for an inline sparkline. Needs at least two points */
export function sparkPath(values: number[], width: number, height: number, pad = 3): ISparkGeometry | null {
  if (values.length < 2) {
    return null;
  }

  const min = Math.min(...values);
  const range = (Math.max(...values) - min) || 1;
  const step = (width - 2 * pad) / (values.length - 1);
  const coords = values.map((v, i) => ({
    x: Number((pad + i * step).toFixed(1)),
    y: Number((height - pad - ((v - min) / range) * (height - 2 * pad)).toFixed(1)),
  }));
  const line = `M${coords.map(c => `${c.x},${c.y}`).join(` L`)}`;
  const last = coords[coords.length - 1];

  return { line, area: `${line} L${last.x},${height} L${coords[0].x},${height} Z`, last };
}

/** "4 Oct to 2 Nov 2026", with the first year shown only when the range crosses a year */
export function dateRangeLabel(start: string, end: string) {
  const s = DateTime.fromISO(start);
  const e = DateTime.fromISO(end);

  return `${s.toFormat(s.year === e.year ? `d LLL` : `d LLL yyyy`)} to ${e.toFormat(`d LLL yyyy`)}`;
}

export function weekLabel(weekStart: string) {
  return DateTime.fromISO(weekStart).toFormat(`LLL d`);
}

export function relativeTime(iso: string | null) {
  return iso ? DateTime.fromISO(iso).toRelative() ?? `Never` : `Never`;
}

export function daysSince(iso: string | null) {
  return iso ? Math.floor(DateTime.now().diff(DateTime.fromISO(iso), `days`).days) : null;
}
